import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axiosConfig from '../../data/utils/axiosConfigInstance';
import { buildRoutes } from '../../service/buildRoutes';
import Toast from '../../utils/toastManager';

function MyAccountUpdateDetailsModal({ open, onClose, onSubmit, submitting = false, initialValues, isSubuser = false, userId }) {
	const navigate = useNavigate();
	const [formValues, setFormValues] = useState({
		firstname: '',
		lastname: '',
		email: ''
	});
	const [passwordResetMode, setPasswordResetMode] = useState(false);
	const [passwordResetStep, setPasswordResetStep] = useState(1);
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [verifyingPassword, setVerifyingPassword] = useState(false);

	useEffect(() => {
		if (!open) return;

		setFormValues({
			firstname: initialValues?.firstname || '',
			lastname: initialValues?.lastname || '',
			email: initialValues?.email?.toLowerCase() || ''
		});
		setPasswordResetMode(false);
		setPasswordResetStep(1);
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');
	}, [open, initialValues?.firstname, initialValues?.lastname, initialValues?.email, userId]);

	if (!open) return null;

	const handleSubmit = async event => {
		event.preventDefault();
		if (submitting) return;

		await onSubmit({
			firstname: formValues.firstname,
			lastname: formValues.lastname,
			email: formValues.email
		});
	};

	const updateField = (field, value) => {
		const nextValue = field === 'email' ? value.toLowerCase() : value;
		setFormValues(prev => ({
			...prev,
			[field]: nextValue
		}));
	};

	const handleVerifyCurrentPassword = async () => {
		if (!currentPassword.trim()) {
			Toast.error('Please enter your current password');
			return;
		}

		setVerifyingPassword(true);
		try {
			await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/verify/password`, {
				userId,
				password: currentPassword
			});
			setPasswordResetStep(2);
			Toast.success('Password verified. Enter your new password.');
		} catch (error) {
			Toast.error(error.response?.data?.message || 'Current password is incorrect');
		} finally {
			setVerifyingPassword(false);
		}
	};

	const handleResetPassword = async () => {
		if (!newPassword.trim()) {
			Toast.error('Please enter a new password');
			return;
		}

		if (newPassword !== confirmPassword) {
			Toast.error('Passwords do not match');
			return;
		}

		if (newPassword.length < 6) {
			Toast.error('Password must be at least 6 characters');
			return;
		}

		setVerifyingPassword(true);
		try {
			await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/update/password`, {
				userId,
				newPassword
			});
			Toast.success('Password updated successfully');
			setPasswordResetMode(false);
			setPasswordResetStep(1);
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
		} catch (error) {
			Toast.error(error.response?.data?.message || 'Failed to update password');
		} finally {
			setVerifyingPassword(false);
		}
	};

	const handleCancelPasswordReset = () => {
		setPasswordResetMode(false);
		setPasswordResetStep(1);
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');
	};

	const handleResetPasswordRedirect = () => {
		onClose();
		navigate(buildRoutes.toForgotPassword(), {
			state: {
				from: '/account'
			}
		});
	};

	return createPortal(
		<div className="manage-subscription-modal__overlay" role="dialog" aria-modal="true" aria-labelledby="update-account-title">
			<div className="manage-subscription-modal__panel">
				<div className="manage-subscription-modal__header">
					<div>
						<h3 id="update-account-title">
							{passwordResetMode ? 'Reset Password' : 'Update Account Details'}
						</h3>
						<p>
							{passwordResetMode
								? passwordResetStep === 1
									? 'Enter your current password to proceed'
									: 'Enter your new password'
								: 'Edit your profile details here.'}
						</p>
					</div>
					<button type="button" className="manage-subscription-modal__close" onClick={onClose} disabled={submitting || verifyingPassword}>
						x
					</button>
				</div>

				{!passwordResetMode ? (
					<form className="manage-subscription-modal__form" onSubmit={handleSubmit}>
						<div className="account-v2-edit-modal__grid">
							<label className="manage-subscription-modal__field">
								<span>FIRST NAME</span>
								<input
									type="text"
									value={formValues.firstname}
									onChange={event => updateField('firstname', event.target.value)}
									disabled={submitting}
									required
								/>
							</label>

							<label className="manage-subscription-modal__field">
								<span>LAST NAME</span>
								<input
									type="text"
									value={formValues.lastname}
									onChange={event => updateField('lastname', event.target.value)}
									disabled={submitting}
									required
								/>
							</label>

							<label className="manage-subscription-modal__field account-v2-edit-modal__field--full">
								<span>EMAIL ADDRESS</span>
								<input
									type="email"
									value={formValues.email}
									onChange={event => updateField('email', event.target.value)}
									disabled={submitting || isSubuser}
									required
								/>
							</label>
						</div>

						{isSubuser && (
							<div className="manage-subscription-modal__notice">
								Sub-user email addresses can only be updated by the studio owner.
							</div>
						)}

						<div className="manage-subscription-modal__confirm-actions">
							<button
								type="button"
								className="manage-subscription-modal__confirm-cancel"
								onClick={handleResetPasswordRedirect}
								disabled={submitting}
							>
								Change Password
							</button>
							<button type="submit" className="manage-subscription-modal__confirm-submit" disabled={submitting}>
								{submitting ? 'Saving...' : 'Update'}
							</button>
						</div>
					</form>
				) : (
					<form className="manage-subscription-modal__form">
						{passwordResetStep === 1 ? (
							<label className="manage-subscription-modal__field account-v2-edit-modal__field--full">
								<span>CURRENT PASSWORD</span>
								<input
									type="password"
									value={currentPassword}
									onChange={event => setCurrentPassword(event.target.value)}
									disabled={verifyingPassword}
									placeholder="Enter your current password"
									required
								/>
							</label>
						) : (
							<>
								<label className="manage-subscription-modal__field account-v2-edit-modal__field--full">
									<span>NEW PASSWORD</span>
									<input
										type="password"
										value={newPassword}
										onChange={event => setNewPassword(event.target.value)}
										disabled={verifyingPassword}
										placeholder="Enter new password"
										required
									/>
								</label>
								<label className="manage-subscription-modal__field account-v2-edit-modal__field--full">
									<span>CONFIRM PASSWORD</span>
									<input
										type="password"
										value={confirmPassword}
										onChange={event => setConfirmPassword(event.target.value)}
										disabled={verifyingPassword}
										placeholder="Confirm new password"
										required
									/>
								</label>
							</>
						)}

						<div className="manage-subscription-modal__confirm-actions">
							<button
								type="button"
								className="manage-subscription-modal__confirm-cancel"
								onClick={handleCancelPasswordReset}
								disabled={verifyingPassword}
							>
								Back
							</button>
							{passwordResetStep === 1 ? (
								<button
									type="button"
									className="manage-subscription-modal__confirm-submit"
									onClick={handleVerifyCurrentPassword}
									disabled={verifyingPassword}
								>
									{verifyingPassword ? 'Verifying...' : 'Verify Password'}
								</button>
							) : (
								<button
									type="button"
									className="manage-subscription-modal__confirm-submit"
									onClick={handleResetPassword}
									disabled={verifyingPassword}
								>
									{verifyingPassword ? 'Updating...' : 'Update Password'}
								</button>
							)}
						</div>
					</form>
				)}
			</div>
		</div>,
		document.body
	);
}

export default MyAccountUpdateDetailsModal;
